/**
 * Color Drop Zone Element Schema
 * Interactive element: background image with hotspot zones, draggable objects with color selection
 *
 * Modes:
 * - drag_and_color: Drag objects from tray to zones, then select color for each
 * - color_only: Objects are pre-placed in zones (embedded in background); learner only selects color
 */

import type { ElementSchema } from '@ai-content-hub/content-kit';
import type { BlockContent, BlockContentType } from '../block';
import { createDefaultBlockContent } from '../block';
import { registerSchemaI18n } from '../i18n/registerSchemaI18n';

/** Color-drop-zone UI strings; exposed on {@link colorDropZoneElementSchema}.i18n */
export const colorDropZoneElementI18nEn = {
    addZone: 'Add zone',
    selectColor: 'Select color',
    dropHere: 'Drop here',
    content: 'Content',
    cdzCanvas: 'Canvas',
    cdzChangeBackground: 'Change background',
    cdzRemoveBackground: 'Remove background',
    cdzOpacity: 'Opacity',
    cdzToolbarColor: 'Color',
    cdzZonesLabel: 'zones',
    cdzEmptyCanvasHint: 'Click here or use "Change background" button',
    cdzBgRequiredWarning:
        'Please upload a background image before adding objects.',
    cdzDialogChangeBackgroundTitle: 'Change background image',
    cdzObjectsHeading: 'Objects',
    cdzObjectNameLabel: 'Object name',
    cdzObjectNamePlaceholder: 'Object name (e.g., Hat, Shirt...)',
    cdzCorrectColorLabel: 'Correct color',
    cdzClickCanvasForOpacityHint: 'Click object on canvas to change opacity',
    cdzAdvancedToggle: 'Advanced',
    cdzExplanationFeedbackLabel: 'Explanation (feedback)',
    cdzExplanationPlaceholder: 'Shown when the object is incorrect...',
    cdzAddObject: 'Add Object',
    cdzResizeHotspotAria: 'Resize',
    cdzObjectFallbackName: 'Object',
    cdzBackgroundAlt: 'Background',
    cdzPlayerNoContent: 'No content',
} as const satisfies Record<string, string>;

export const colorDropZoneElementI18nVi = {
    addZone: 'Thêm vùng',
    selectColor: 'Chọn màu',
    dropHere: 'Thả vào đây',
    content: 'Nội dung',
    cdzCanvas: 'Khung hình',
    cdzChangeBackground: 'Đổi nền',
    cdzRemoveBackground: 'Gỡ nền',
    cdzOpacity: 'Độ mờ',
    cdzToolbarColor: 'Màu',
    cdzZonesLabel: 'vùng',
    cdzEmptyCanvasHint: 'Bấm vào đây hoặc dùng nút "Đổi nền"',
    cdzBgRequiredWarning:
        'Vui lòng tải ảnh nền trước khi thêm đối tượng.',
    cdzDialogChangeBackgroundTitle: 'Đổi ảnh nền',
    cdzObjectsHeading: 'Đối tượng',
    cdzObjectNameLabel: 'Tên đối tượng',
    cdzObjectNamePlaceholder: 'Tên (vd. mũ, áo...)',
    cdzCorrectColorLabel: 'Màu đúng',
    cdzClickCanvasForOpacityHint:
        'Bấm đối tượng trên khung để chỉnh độ mờ',
    cdzAdvancedToggle: 'Nâng cao',
    cdzExplanationFeedbackLabel: 'Giải thích (phản hồi)',
    cdzExplanationPlaceholder: 'Hiển thị khi đối tượng trả lời sai...',
    cdzAddObject: 'Thêm đối tượng',
    cdzResizeHotspotAria: 'Đổi kích thước',
    cdzObjectFallbackName: 'Đối tượng',
    cdzBackgroundAlt: 'Ảnh nền',
    cdzPlayerNoContent: 'Không có nội dung',
} as const satisfies Record<string, string>;

export type ColorDropZoneElementI18nKey = keyof typeof colorDropZoneElementI18nEn;

export const defaultColorDropZoneElementI18n = {
    en: { ...colorDropZoneElementI18nEn },
    vi: { ...colorDropZoneElementI18nVi },
};

registerSchemaI18n(defaultColorDropZoneElementI18n);

export interface DropZoneHotspot {
    id: string;
    label: string;
    x: number;
    y: number;
    radius: number;
    linkedObjectId?: string;
}

export interface ColorOption {
    id: string;
    label: string;
    value: string;
}

export interface DropZoneObject {
    id: string;
    name: string;
    content: BlockContent;
    correctColorId: string;
    correctZoneId: string;
    explanation?: string;
    opacity?: number;
}

/** Player mode: drag objects + color, or color-only (objects pre-placed) */
export type ColorDropZoneMode = 'drag_and_color' | 'color_only';

export interface ColorDropZoneElementData {
    background: BlockContent;
    zones: DropZoneHotspot[];
    colorPalette: ColorOption[];
    objects: DropZoneObject[];
    /** Player mode: drag_and_color (default) or color_only */
    mode?: ColorDropZoneMode;
    allowedObjectTypes?: BlockContentType[];
    objectMinSize?: number;
    objectMaxSize?: number;
    shuffleObjects?: boolean;
}

export type ColorDropZoneAnswer = Record<string, { zoneId: string; colorId: string }>;

const DEFAULT_OBJECT_MIN_SIZE = 48;
const DEFAULT_OBJECT_MAX_SIZE = 120;

export function getBackgroundImageSrc(background: BlockContent | undefined): string {
    if (!background || background.type !== 'image') return '';
    return (background as { src?: string }).src ?? '';
}

export function getBackgroundImageAlt(background: BlockContent | undefined): string {
    if (!background || background.type !== 'image') return '';
    return (background as { alt?: string }).alt ?? '';
}

export function createDefaultHotspot(
    id?: string,
    options?: { label?: string; linkedObjectId?: string }
): DropZoneHotspot {
    return {
        id: id ?? `zone-${Date.now()}`,
        label: options?.label ?? '',
        x: 50,
        y: 50,
        radius: 80,
        linkedObjectId: options?.linkedObjectId,
    };
}

export function createDefaultColorOption(id?: string): ColorOption {
    return {
        id: id ?? `color-${Date.now()}`,
        label: '',
        value: '#3B82F6',
    };
}

export function createDefaultDropZoneObject(id?: string): DropZoneObject {
    return {
        id: id ?? `obj-${Date.now()}`,
        name: '',
        content: createDefaultBlockContent('image'),
        correctColorId: '',
        correctZoneId: '',
        opacity: 1,
    };
}

export function normalizeColorDropZoneData(
    data: Partial<ColorDropZoneElementData>
): ColorDropZoneElementData {
    const background: BlockContent =
        data.background && typeof data.background === 'object' && 'type' in data.background
            ? data.background
            : createDefaultBlockContent('image');

    const zones: DropZoneHotspot[] = (data.zones ?? []).map((z, i) => ({
        id: z.id ?? `zone-${i + 1}`,
        label: (z.label ?? '').trim() || `Zone ${i + 1}`,
        x: typeof z.x === 'number' ? z.x : 50,
        y: typeof z.y === 'number' ? z.y : 50,
        radius: typeof z.radius === 'number' ? Math.max(20, Math.min(200, z.radius)) : 80,
        linkedObjectId: z.linkedObjectId,
    }));

    const colorPalette: ColorOption[] = (data.colorPalette ?? []).map((c, i) => ({
        id: c.id ?? `color-${i + 1}`,
        label: c.label ?? `Color ${i + 1}`,
        value: c.value ?? '#3B82F6',
    }));

    const objects: DropZoneObject[] = (data.objects ?? []).map((o, i) => {
        let content: BlockContent;
        if (o.content && typeof o.content === 'object' && 'type' in o.content) {
            content = o.content;
        } else {
            content = createDefaultBlockContent('image');
        }
        return {
            id: o.id ?? `obj-${i + 1}`,
            name: o.name ?? '',
            content,
            correctColorId: o.correctColorId ?? '',
            correctZoneId: o.correctZoneId ?? '',
            explanation: o.explanation,
            opacity:
                typeof o.opacity === 'number'
                    ? Math.max(0, Math.min(1, o.opacity))
                    : 1,
        };
    });

    return {
        background,
        zones,
        colorPalette,
        objects,
        mode: data.mode ?? 'drag_and_color',
        allowedObjectTypes: data.allowedObjectTypes ?? ['image'],
        objectMinSize: data.objectMinSize ?? DEFAULT_OBJECT_MIN_SIZE,
        objectMaxSize: data.objectMaxSize ?? DEFAULT_OBJECT_MAX_SIZE,
        shuffleObjects: data.shuffleObjects ?? true,
    };
}

export const colorDropZoneElementSchema: ElementSchema<ColorDropZoneElementData> = {
    type: 'element-color-drop-zone',
    title: 'Color Drop Zone',
    description:
        'Place draggable objects on correct zones and select the right color for each.',
    version: '1.0.0',
    category: 'interactive',
    icon: 'Palette',
    gradable: true,
    tags: ['color', 'drop-zone', 'drag-drop', 'placement', 'interactive', 'coloring'],
    fields: [],
    defaultData: {
        background: createDefaultBlockContent('image'),
        zones: [],
        colorPalette: [
            { id: 'red', label: 'Red', value: '#EF4444' },
            { id: 'blue', label: 'Blue', value: '#3B82F6' },
            { id: 'green', label: 'Green', value: '#22C55E' },
        ],
        objects: [],
        allowedObjectTypes: ['image'],
        objectMinSize: DEFAULT_OBJECT_MIN_SIZE,
        objectMaxSize: DEFAULT_OBJECT_MAX_SIZE,
        shuffleObjects: true,
    },
    i18n: defaultColorDropZoneElementI18n,
};
